import { FlexBox, IconButton, Text, Tooltip } from '@dixa/lib-shared-habla';
import { ampli } from 'lib-shared-tracking';
import { useCallback } from 'react';

import { CommandKeybindingsTooltipContent } from 'components/commandKeybindingsTooltipContent';

import type { SidebarMode } from './conversationSidebar.types';
import styles from './sidebarToggleTrigger.module.scss';

type SidebarToggleTriggerProps = {
  isMenuOpen: boolean;
  toggleSidebar: () => void;
  sidebarMode: SidebarMode;
  setSidebarMode: (mode: SidebarMode) => void;
  /** Whether the agent may enter customization (effective personalization). */
  canCustomize: boolean;
};

export const SidebarToggleTrigger = ({
  isMenuOpen,
  toggleSidebar,
  sidebarMode,
  setSidebarMode,
  canCustomize,
}: SidebarToggleTriggerProps): JSX.Element => {
  const handleToggle = useCallback(() => {
    if (!isMenuOpen) {
      ampli.conversationSidebarOpened();
    } else {
      ampli.conversationSidebarClosed();
    }

    toggleSidebar();
  }, [isMenuOpen, toggleSidebar]);

  const tooltipContent = (
    <CommandKeybindingsTooltipContent commandName="conversation:toggleRightSidebar">
      {isMenuOpen ? 'Close' : 'Show'}
    </CommandKeybindingsTooltipContent>
  );

  return (
    <FlexBox flexDirection="column" className={styles.root}>
      <FlexBox
        alignItems="center"
        justifyContent="space-between"
        padding="200 300"
      >
        {isMenuOpen && (
          <FlexBox
            justifyContent="space-between"
            alignItems="center"
            gap="300"
            flex="1"
            padding="0 100 0 0"
          >
            {sidebarMode === 'customization' ? (
              <Text.ExternalLink
                onClick={() => setSidebarMode('default')}
                className={styles.backLink}
              >
                {'\u2039'} Back
              </Text.ExternalLink>
            ) : (
              <>
                <Text.Paragraph variant="1">Details</Text.Paragraph>
                {canCustomize && (
                  <Text.ExternalLink
                    data-conversation-sidebar-helper
                    onClick={() => {
                      ampli.conversationRightSidebarCustomizeClicked();
                      setSidebarMode('customization');
                    }}
                  >
                    Customize
                  </Text.ExternalLink>
                )}
              </>
            )}
          </FlexBox>
        )}

        <Tooltip content={tooltipContent}>
          <IconButton
            variant="tertiary"
            onClick={handleToggle}
            icon={isMenuOpen ? 'angle-double-right' : 'angle-double-left'}
          />
        </Tooltip>
      </FlexBox>
    </FlexBox>
  );
};
